<template lang="pug">
  label.checkbox(v-bind="bindAttrs" :class="{disabled: disabled, checked: modelValue }")
    input( v-bind="bindInputAttrs" type="checkbox" :value="modelValue" @input="change()" :disabled="disabled")
    .box
      Icon.icon-size-xs(name="check" v-if="modelValue" :fill="disabled ? 'lightgray' : 'black'")
    .primary(
      v-if="label"
      :class="{'primary-required': required, 'input-error': errors && errors.length > 0}"
    ) {{__(label, labelArgs ?? {}, 'auto')}}
      span.block.required(v-if="required") *
      .error-message(v-if="errorDisplay && touched && errors && errors.length > 0")
        | {{__(errors[0], 'auto')}}
    slot
    .secondary(v-if="secondary") {{__(secondary, 'auto')}}
</template>
<script setup lang="ts">
import { computed, ref, watch, useAttrs } from 'vue'
import { useRogue } from '../../js/rogue'

defineOptions({
  inheritAttrs: false,
})

type CheckboxProps = {
  /** The label of the checkbox. */
  label?: string | null
  /** Interpolation args for the label translation. */
  labelArgs?: Record<string, any> | null
  /** Is the input required? */
  required?: boolean | string
  /** Whether to display validation errors. */
  errorDisplay?: boolean
  /** The secondary (float: right) text for the checkbox. */
  secondary?: string | null
  /** The bound value (v-model) of the checkbox. */
  modelValue?: boolean | string | null
  /** Whether the checkbox is disabled or not. */
  disabled?: boolean
  name?: string
}

const props = withDefaults(defineProps<CheckboxProps>(), {
  label        : null,
  labelArgs    : null,
  required     : false,
  errorDisplay : true,
  secondary    : null,
  modelValue   : null,
  disabled     : false,
  name         : '',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const attrs = useAttrs()

const touched = ref<boolean>(false)
const errors = ref<string[]>([])

const bindAttrs = computed(() => ({
  class : (attrs as any).class,
  style : (attrs as any).style,
  for   : (attrs as any).id,
}))

const bindInputAttrs = computed(() => ({
  id: (attrs as any).id,
}))

const rogue = useRogue()

function validate() {
  const newErrors: string[] = []
  const val = props.modelValue
  if (props.required && val !== true) {
    newErrors.push(rogue.__('Required Field'))
  }
  errors.value = newErrors
}

function change() {
  emit('update:modelValue', !props.modelValue)
  touched.value = true
}

watch(
  () => props.modelValue,
  () => {
    validate()
  },
  { immediate: true }
)
</script>

<style lang="scss" scoped>
// Hide the default checkbox
.checkbox input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  width: 0;
  height: 0;
}

.box {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid $color-black;
  border-radius: 2px;
  background-color: $color-white;
  min-height: $size-m;
  min-width: $size-m;
  margin-right: $space-xxs;
}

.checkbox.checked {
  .box {
    border-color: $color-blue;
    background-color: $color-blue-light;
  }

  .icon {
    display: block;
    transform: scale(1);
  }
}

.checkbox.checked.disabled {
  .box {
    border-color: $color-gray-light;
    background-color: $color-gray-light;
  }
}

.checkbox {
  font-size: $size-xs;
  line-height: $line-height-m;
  position: relative;
  display: flex;
  cursor: pointer;
  user-select: none;
  transition: color 0.1s;
  align-items: center;

  &:hover {
    .box {
      border-color: $color-gray;
    }
  }

  &:focus-within {
    .box {
      border-color: $color-black;
    }
  }
}

.icon {
  display: none;
}

.primary {
  flex-grow: 1;

  .required {
    color: $color-red;
    display: inline-block;
    margin-left: $space-xxs;
  }
}

.disabled {
  color: $color-gray
}

.error-message {
  position: absolute;
  top: 15px;
  color: $color-red;
  font-size: $size-xxs;
}

</style>

<style lang="scss" scoped target="app">

.platform-ios, .platform-android {
  .checkbox {
    min-height: $app-checkbox-min-height;
    vertical-align: center;

    .box {
      margin-right: 10px;
      position: relative;
    }

    &:not(.disabled) .box {
      border-color: $color-green;
    }

    &.checked .icon {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(10deg);
    }
  }
}

.platform-ios {
  .checkbox {
    padding: $app-checkbox-padding-ios;

    .box {
      height: $app-ios-checkbox-size;
      width: $app-ios-checkbox-size;
      min-width: $app-ios-checkbox-size;
      border-radius: 50%;
    }

    &.checked {
      .box {
        background: none;
      }

      .icon:deep(svg) {
        fill: $color-green !important;
      }
    }
  }
}

.platform-android {
  .checkbox {
    padding: $app-checkbox-padding-default;

    .box {
      height: $app-android-checkbox-size;
      width: $app-android-checkbox-size;
      min-width: $app-android-checkbox-size;
    }

    &.checked {
      .box {
        background: $color-green;
      }

      .icon:deep(svg) {
        fill: $color-white !important;
      }
    }
  }
}
</style>
